import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// API base URL from environment variable, falling back to relative path for proxy
const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const REFRESH_TOKEN_KEY = 'weticket_refresh_token';

let accessToken: string | null = null;
let refreshPromise: Promise<string> | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function setRefreshToken(token: string | null) {
  if (token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Prevent multiple simultaneous refresh requests
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken();
      }

      try {
        const newToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      } finally {
        refreshPromise = null;
      }
    }

    return Promise.reject(error);
  }
);

async function refreshAccessToken(): Promise<string> {
  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await axios.post(
    `${API_BASE_URL}/auth/refresh`,
    { refreshToken: currentRefreshToken },
    { withCredentials: true }
  );
  const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data;
  setAccessToken(newAccessToken);
  setRefreshToken(newRefreshToken);
  return newAccessToken;
}

// Normalize API errors for consistent handling
export interface ApiError {
  message: string;
  statusCode: number;
  error?: string;
}

export function normalizeError(error: unknown): ApiError {
  if (axios.isAxiosError(error) && error.response?.data) {
    const data = error.response.data as Record<string, unknown>;
    const raw = data.message;
    let message: string;
    if (Array.isArray(raw)) {
      message = raw.filter((x) => typeof x === 'string' && x.trim()).join(' ');
    } else if (typeof raw === 'string') {
      message = raw;
    } else {
      message = 'An error occurred';
    }
    return {
      message: message || 'An error occurred',
      statusCode: error.response.status,
      error: data.error as string | undefined,
    };
  }
  return {
    message: error instanceof Error ? error.message : 'An unknown error occurred',
    statusCode: 500,
  };
}

/** Backend returns this `code` with HTTP 409 when deleting a status that still has tasks. */
export const STATUS_DELETE_HAS_TASKS_CODE = 'STATUS_HAS_TASKS' as const;

export type StatusDeleteTasksConflictBody = {
  code?: string;
  message?: string;
  taskCount?: number;
};

export function isStatusDeleteTasksConflict(
  error: unknown
): error is AxiosError<StatusDeleteTasksConflictBody> {
  if (!axios.isAxiosError(error) || error.response?.status !== 409) return false;
  const data = error.response.data as StatusDeleteTasksConflictBody | undefined;
  return data?.code === STATUS_DELETE_HAS_TASKS_CODE;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  displayName: string;
}

export const authApi = {
  login: (data: LoginRequest) => api.post('/auth/login', data),
  register: (data: RegisterRequest) => api.post('/auth/register', data),
  logout: (refreshTokenValue?: string) => api.post('/auth/logout', refreshTokenValue ? { refreshToken: refreshTokenValue } : {}),
  refresh: (refreshTokenValue: string) => api.post('/auth/refresh', { refreshToken: refreshTokenValue }),
};

export interface UpdateProfileData {
  displayName?: string;
  avatarUrl?: string;
}

export interface AdminUpdateUserData {
  displayName?: string;
  publicName?: string | null;
  email?: string;
  avatarUrl?: string | null;
  role?: string;
  isActive?: boolean;
  emailNotificationsEnabled?: boolean;
}

export const usersApi = {
  getMe: () => api.get('/users/me'),
  getAll: () => api.get('/users'),
  getById: (id: string) => api.get(`/users/${id}`),
  update: (id: string, data: UpdateProfileData) => api.patch(`/users/${id}`, data),
  uploadMyAvatar: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/users/me/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  removeMyAvatar: () => api.delete('/users/me/avatar'),
  updateRole: (id: string, role: string) => api.patch(`/users/${id}/role`, { role }),
  deactivate: (id: string) => api.delete(`/users/${id}`),
  activate: (id: string) => api.post(`/users/${id}/activate`),
  // Admin management endpoints
  getAdminDetails: (id: string) => api.get(`/users/${id}/admin`),
  adminUpdate: (id: string, data: AdminUpdateUserData) => api.patch(`/users/${id}/admin`, data),
  adminSetPassword: (id: string, password: string) =>
    api.post(`/users/${id}/admin/set-password`, { password }),
  adminSendPasswordReset: (id: string) => api.post(`/users/${id}/admin/send-password-reset`),
  adminUpdateTeams: (id: string, teamIds: string[]) =>
    api.patch(`/users/${id}/admin/teams`, { teamIds }),
  adminUpdateProjects: (id: string, projectIds: string[]) =>
    api.patch(`/users/${id}/admin/projects`, { projectIds }),
  changePassword: (currentPassword: string, newPassword: string) =>
    api.post('/users/me/change-password', { currentPassword, newPassword }),
};

export const projectsApi = {
  getAll: (params?: { includeArchived?: string }) =>
    api.get('/projects', { params }),
  getById: (id: string) => api.get(`/projects/${id}`),
  getDashboard: (id: string) => api.get(`/projects/${id}/dashboard`),
  create: (data: { name: string; description?: string; color?: string }) => api.post('/projects', data),
  update: (id: string, data: { name?: string; description?: string; color?: string }) =>
    api.patch(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  archive: (id: string) => api.post(`/projects/${id}/archive`),
  unarchive: (id: string) => api.post(`/projects/${id}/unarchive`),
  getStatuses: (projectId: string) => api.get(`/projects/${projectId}/statuses`),
  createStatus: (projectId: string, data: { name: string; color?: string; isDoneState?: boolean }) =>
    api.post(`/projects/${projectId}/statuses`, data),
  updateStatus: (projectId: string, statusId: string, data: { name?: string; color?: string; isDoneState?: boolean }) =>
    api.patch(`/projects/${projectId}/statuses/${statusId}`, data),
  reorderStatuses: (projectId: string, statusIds: string[]) =>
    api.post(`/projects/${projectId}/statuses/reorder`, { statusIds }),
  deleteStatus: (projectId: string, statusId: string, replacementStatusId?: string) =>
    api.delete(`/projects/${projectId}/statuses/${statusId}`, {
      params: replacementStatusId ? { replacementStatusId } : undefined,
    }),
  getMembers: (projectId: string) => api.get(`/projects/${projectId}/members`),
  addMember: (projectId: string, data: { userId: string; role?: string }) =>
    api.post(`/projects/${projectId}/members`, data),
  updateMemberRole: (projectId: string, memberId: string, data: { role: string }) =>
    api.patch(`/projects/${projectId}/members/${memberId}`, data),
  removeMember: (projectId: string, memberId: string) =>
    api.delete(`/projects/${projectId}/members/${memberId}`),
};

export const listsApi = {
  getByProject: (projectId: string, params?: { includeArchived?: string }) =>
    api.get(`/projects/${projectId}/lists`, { params }),
  getById: (id: string) => api.get(`/lists/${id}`),
  create: (projectId: string, data: { name: string }) =>
    api.post(`/projects/${projectId}/lists`, data),
  update: (id: string, data: { name?: string }) => api.patch(`/lists/${id}`, data),
  delete: (id: string) => api.delete(`/lists/${id}`),
  archive: (id: string) => api.post(`/lists/${id}/archive`),
  unarchive: (id: string) => api.post(`/lists/${id}/unarchive`),
  reorder: (projectId: string, listIds: string[]) =>
    api.post(`/projects/${projectId}/lists/reorder`, { listIds }),
  
  // Status management for lists
  getStatuses: (listId: string) => api.get(`/lists/${listId}/statuses`),
  enableCustomStatuses: (listId: string) =>
    api.post(`/lists/${listId}/statuses/enable-custom`),
  disableCustomStatuses: (listId: string) =>
    api.post(`/lists/${listId}/statuses/disable-custom`),
  createStatus: (listId: string, data: { name: string; color?: string; isDoneState?: boolean }) =>
    api.post(`/lists/${listId}/statuses`, data),
  updateStatus: (listId: string, statusId: string, data: { name?: string; color?: string; isDoneState?: boolean }) =>
    api.patch(`/lists/${listId}/statuses/${statusId}`, data),
  deleteStatus: (listId: string, statusId: string, replacementStatusId?: string) =>
    api.delete(`/lists/${listId}/statuses/${statusId}`, {
      params: replacementStatusId ? { replacementStatusId } : undefined,
    }),
  reorderStatuses: (listId: string, statusIds: string[]) =>
    api.post(`/lists/${listId}/statuses/reorder`, { statusIds }),
  getTaskDuplicates: (listId: string, title: string) =>
    api.get<ListTaskDuplicatesResponse>(`/lists/${listId}/tasks/duplicates`, { params: { title } }),
};

export interface PlanningWeeksParams {
  limit?: number;
  cursor?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
}

export interface CreatePlanningItemData {
  weekId: string;
  agentId: string;
  date: string;
  projectId?: string;
  listId?: string;
  customWorkGroupId?: string;
  customActivityId?: string;
  shift?: 'morning' | 'afternoon';
  role?: 'primary' | 'cover';
  description?: string;
}

export interface UpdatePlanningItemData {
  projectId?: string;
  listId?: string;
  customWorkGroupId?: string;
  customActivityId?: string;
  shift?: 'morning' | 'afternoon' | null;
  role?: 'primary' | 'cover' | null;
  description?: string;
}

export interface CreatePlanningWorkGroupData {
  name: string;
  description?: string;
}

export interface UpdatePlanningWorkGroupData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface CreatePlanningActivityData {
  name: string;
  description?: string;
}

export interface UpdatePlanningActivityData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export interface MovePlanningItemData {
  agentId: string;
  date: string;
  position: number;
}

export interface DuplicatePlanningItemData {
  duplicateForRestOfWeek?: boolean;
}

export interface UpdatePlanningDescriptionData {
  description?: string;
}

export interface UpdatePlanningWeekNoteData {
  note?: string;
}

export interface UpdatePlanningWeekLiveData {
  isLive: boolean;
}

export interface SubmitPlanningWorklogData {
  planningItemId: string;
  mailsProcessed: number;
  note?: string;
}

export const planningApi = {
  getWeeks: (params?: PlanningWeeksParams) => api.get('/planning/weeks', { params }),
  getWeek: (id: string) => api.get(`/planning/weeks/${id}`),
  generateWeek: (startDate: string) => api.post('/planning/weeks/generate', { startDate }),
  createItem: (data: CreatePlanningItemData) => api.post('/planning/items', data),
  updateItem: (id: string, data: UpdatePlanningItemData) => api.patch(`/planning/items/${id}`, data),
  deleteItem: (id: string) => api.delete(`/planning/items/${id}`),
  duplicateItem: (id: string, data: DuplicatePlanningItemData) =>
    api.post(`/planning/items/${id}/duplicate`, data),
  moveItem: (id: string, data: MovePlanningItemData) => api.patch(`/planning/items/${id}/move`, data),
  archiveWeek: (id: string) => api.patch(`/planning/weeks/${id}/archive`),
  unarchiveWeek: (id: string) => api.patch(`/planning/weeks/${id}/unarchive`),
  deleteWeek: (id: string) => api.delete(`/planning/weeks/${id}`),
  updateWeekNote: (id: string, data: UpdatePlanningWeekNoteData) =>
    api.patch(`/planning/weeks/${id}/note`, data),
  updateWeekLiveStatus: (id: string, data: UpdatePlanningWeekLiveData) =>
    api.patch(`/planning/weeks/${id}/live`, data),
  updateDescription: (id: string, data: UpdatePlanningDescriptionData) =>
    api.patch(`/planning/items/${id}/description`, data),
  submitWorklog: (data: SubmitPlanningWorklogData) => api.post('/planning/worklogs', data),
  getWorklogs: (weekId: string) => api.get('/planning/worklogs', { params: { weekId } }),
  getProduction: (weekId: string) => api.get('/planning/production', { params: { weekId } }),
  // Work Groups Catalog
  getWorkGroups: (includeInactive?: boolean) =>
    api.get('/planning/work-groups', { params: includeInactive ? { includeInactive: 'true' } : undefined }),
  getWorkGroup: (id: string) => api.get(`/planning/work-groups/${id}`),
  createWorkGroup: (data: CreatePlanningWorkGroupData) => api.post('/planning/work-groups', data),
  updateWorkGroup: (id: string, data: UpdatePlanningWorkGroupData) =>
    api.patch(`/planning/work-groups/${id}`, data),
  deleteWorkGroup: (id: string) => api.delete(`/planning/work-groups/${id}`),
  // Activities Catalog
  getActivities: (includeInactive?: boolean) =>
    api.get('/planning/activities', { params: includeInactive ? { includeInactive: 'true' } : undefined }),
  getActivity: (id: string) => api.get(`/planning/activities/${id}`),
  createActivity: (data: CreatePlanningActivityData) => api.post('/planning/activities', data),
  updateActivity: (id: string, data: UpdatePlanningActivityData) =>
    api.patch(`/planning/activities/${id}`, data),
  deleteActivity: (id: string) => api.delete(`/planning/activities/${id}`),
};

export interface ListTaskDuplicateStatus {
  id: string;
  name: string;
}

export interface ListTaskDuplicateMergedIntoTask {
  id: string;
  title: string;
  listId: string;
  projectId: string;
  status: ListTaskDuplicateStatus;
}

export interface ListTaskDuplicateAssignee {
  id: string;
  displayName: string;
  avatarUrl: string | null;
}

export interface ListTaskDuplicateMatch {
  id: string;
  title: string;
  status: ListTaskDuplicateStatus;
  assignee?: ListTaskDuplicateAssignee;
  updatedAt: string;
  effectiveLastUpdated?: string;
  isMerged: boolean;
  mergedIntoId?: string | null;
  mergedInto?: ListTaskDuplicateMergedIntoTask;
}

export interface ListTaskDuplicatesResponse {
  hasDuplicate: boolean;
  matches: ListTaskDuplicateMatch[];
}

export interface UpdateTaskData {
  title?: string;
  description?: string | null;
  listId?: string;
  statusId?: string;
  priority?: string;
  /** Replaces user assignees when set (use [] to clear). */
  assigneeIds?: string[];
  /** @deprecated Backend still accepts; prefer assigneeIds. */
  assigneeId?: string | null;
  assignedTeamId?: string | null;
  dueDate?: string | null;
  isClientVisible?: boolean;
}

export type TaskVisibilityScope = 'all' | 'my' | 'team';

export const tasksApi = {
  getByProject: (
    projectId: string,
    params?: { listId?: string; visibilityScope?: TaskVisibilityScope },
  ) => {
    const search: Record<string, string> = {
      visibilityScope: params?.visibilityScope ?? 'all',
    };
    const listId = params?.listId?.trim();
    if (listId) {
      search.listId = listId;
    }
    return api.get(`/projects/${projectId}/tasks`, { params: search });
  },
  getByStatus: (projectId: string, statusId: string) =>
    api.get(`/tasks/project/${projectId}/status/${statusId}`),
  getById: (id: string) => api.get(`/tasks/${id}`),
  getFollowing: () => api.get('/tasks/following'),
  follow: (id: string) => api.post(`/tasks/${id}/follow`),
  unfollow: (id: string) => api.delete(`/tasks/${id}/follow`),
  create: (
    projectId: string,
    data: {
      listId: string;
      statusId: string;
      title: string;
      description?: string;
      priority?: string;
      assigneeIds?: string[];
      assigneeId?: string;
      assignedTeamId?: string;
      dueDate?: string;
      parentId?: string;
      isClientVisible?: boolean;
      tagIds?: string[];
    }
  ) => api.post(`/projects/${projectId}/tasks`, data),
  update: (id: string, data: UpdateTaskData) => api.patch(`/tasks/${id}`, data),
  move: (id: string, data: { statusId: string; position?: number }) =>
    api.patch(`/tasks/${id}/move`, data),
  moveTo: (id: string, data: { projectId: string; listId: string; statusId: string }) =>
    api.patch(`/tasks/${id}/move-to`, data),
  assign: (id: string, body: { assigneeIds?: string[]; assigneeId?: string | null; assignedTeamId?: string | null }) =>
    api.patch(`/tasks/${id}/assign`, body),
  reorder: (projectId: string, tasks: { taskId: string; position: number }[]) =>
    api.patch(`/projects/${projectId}/tasks/reorder`, { tasks }),
  delete: (id: string) => api.delete(`/tasks/${id}`),
  getSubtasks: (parentTaskId: string) =>
    api.get<{ subtasks: import('@/types').Task[]; restrictedSubtaskCount: number }>(`/tasks/${parentTaskId}/subtasks`),
  createSubtask: (
    parentTaskId: string,
    data: {
      title: string;
      description?: string;
      listId?: string;
      statusId: string;
      priority?: string;
      assigneeIds?: string[];
      assigneeId?: string | null;
      assignedTeamId?: string | null;
      dueDate?: string | null;
    }
  ) => api.post(`/tasks/${parentTaskId}/subtasks`, data),
  getHistory: (id: string) => api.get(`/tasks/${id}/history`),
  getCustomFields: (taskId: string) => api.get(`/tasks/${taskId}/custom-fields`),
  updateCustomFields: (taskId: string, values: Record<string, unknown>) =>
    api.patch(`/tasks/${taskId}/custom-fields`, { values }),
  merge: (sourceTaskId: string, targetTaskId: string) =>
    api.post(`/tasks/${sourceTaskId}/merge`, { targetTaskId }),
};

export type CustomFieldType = 'text' | 'number' | 'date' | 'select' | 'checkbox';

export interface CustomFieldDefinition {
  id: string;
  scopeType: 'project' | 'list';
  projectId: string | null;
  listId: string | null;
  name: string;
  key: string;
  type: CustomFieldType;
  config: Record<string, unknown> | null;
  position: number;
  isArchived: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TaskCustomFieldsResponse {
  taskId: string;
  fields: Array<{ definition: CustomFieldDefinition; value: unknown }>;
}

export const customFieldsApi = {
  getProjectFields: (projectId: string) => api.get<CustomFieldDefinition[]>(`/projects/${projectId}/custom-fields`),
  createProjectField: (projectId: string, data: { name: string; key: string; type: CustomFieldType; config?: Record<string, unknown>; position?: number }) =>
    api.post<CustomFieldDefinition>(`/projects/${projectId}/custom-fields`, data),
  getListFields: (listId: string) => api.get<CustomFieldDefinition[]>(`/lists/${listId}/custom-fields`),
  getEffectiveForList: (listId: string) =>
    api.get<CustomFieldDefinition[]>(`/lists/${listId}/effective-custom-fields`),
  getTaskCustomFieldValuesForList: (listId: string) =>
    api.get<Record<string, Record<string, unknown>>>(`/lists/${listId}/tasks/custom-fields-values`),
  createListField: (listId: string, data: { name: string; key: string; type: CustomFieldType; config?: Record<string, unknown>; position?: number }) =>
    api.post<CustomFieldDefinition>(`/lists/${listId}/custom-fields`, data),
  updateField: (id: string, data: { name?: string; config?: Record<string, unknown>; position?: number; isArchived?: boolean }) =>
    api.patch<CustomFieldDefinition>(`/custom-fields/${id}`, data),
  deleteField: (id: string) => api.delete(`/custom-fields/${id}`),
};

export interface CreateCommentData {
  content: string;
  visibility?: 'internal' | 'client_visible';
}

export interface UpdateCommentData {
  content?: string;
  visibility?: 'internal' | 'client_visible';
}

export const commentsApi = {
  /** GET /api/tasks/:taskId/comments */
  getByTask: (taskId: string) => api.get(`/tasks/${taskId}/comments`),
  create: (taskId: string, data: CreateCommentData) =>
    api.post(`/tasks/${taskId}/comments`, data),
  update: (id: string, data: UpdateCommentData) => api.patch(`/comments/${id}`, data),
  delete: (id: string) => api.delete(`/comments/${id}`),
};

export const filesApi = {
  /** GET /api/tasks/:taskId/attachments */
  getByTask: (taskId: string) => api.get(`/tasks/${taskId}/attachments`),
  uploadToTask: (taskId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tasks/${taskId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadToComment: (commentId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/comments/${commentId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  download: (id: string) => api.get(`/attachments/${id}/download`, { responseType: 'blob' }),
  /** Remote (R2/S3): returns presigned URL after auth — use before navigation (Bearer required). */
  getRemoteDownloadInfo: (id: string) =>
    api.get<{ url: string; fileName: string; mimeType: string; openMode: 'new-tab' | 'download' }>(
      `/attachments/${id}/download`,
      {
      params: { as: 'json' },
      },
    ),
  getDownloadUrl: (id: string) => `${API_BASE_URL}/attachments/${id}/download`,
  delete: (id: string) => api.delete(`/attachments/${id}`),
};

export const notificationsApi = {
  getAll: (params?: { limit?: number; offset?: number }) =>
    api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id: string) => api.patch(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),
  delete: (id: string) => api.delete(`/notifications/${id}`),
};

export const clickupSyncAdminApi = {
  getSettings: () => api.get('/admin/clickup/settings'),
  updateSettings: (data: { token?: string; spaces: string[] }) =>
    api.post('/admin/clickup/settings', data),
  getAvailableSpaces: () => api.get('/admin/clickup/available-spaces'),
  testConnection: () => api.post('/admin/clickup/test'),
  runNow: () => api.post('/admin/clickup/run'),
  getLogs: (limit = 200) => api.get('/admin/clickup/logs', { params: { limit } }),
  clearLogs: () => api.delete('/admin/clickup/logs'),
};

export interface SearchTasksParams {
  q?: string;
  projectId?: string;
  listId?: string;
  statusId?: string;
  assigneeId?: string;
  dueBefore?: string;
  dueAfter?: string;
  closedBefore?: string;
  closedAfter?: string;
  tagIds?: string[];
}

export const searchApi = {
  search: (query: string, limit?: number) =>
    api.get('/search', { params: { q: query, limit } }),
  searchTasks: (params: SearchTasksParams) =>
    api.get('/search/tasks', { params }),
};

// Invites API
export interface CreateInviteData {
  email: string;
  role?: 'admin' | 'supervisor' | 'user' | 'client';
}

export interface AcceptInviteData {
  token: string;
  displayName: string;
  password: string;
}

export const invitesApi = {
  create: (data: CreateInviteData) => api.post('/invites', data),
  getAll: () => api.get('/invites'),
  validate: (token: string) => api.get(`/invites/validate/${token}`),
  accept: (data: AcceptInviteData) => api.post('/invites/accept', data),
  regenerate: (id: string) => api.post(`/invites/${id}/regenerate`),
  delete: (id: string) => api.delete(`/invites/${id}`),
};

// Teams API
export const teamsApi = {
  getAll: () => api.get('/teams'),
  getById: (id: string) => api.get(`/teams/${id}`),
  create: (data: { name: string; description?: string }) => api.post('/teams', data),
  update: (id: string, data: { name?: string; description?: string }) => api.patch(`/teams/${id}`, data),
  delete: (id: string) => api.delete(`/teams/${id}`),
  getMembers: (id: string) => api.get(`/teams/${id}/members`),
  addMember: (id: string, userId: string) => api.post(`/teams/${id}/members`, { userId }),
  removeMember: (id: string, userId: string) => api.delete(`/teams/${id}/members/${userId}`),
};

// Project teams API (project-team assignments)
export const projectTeamsApi = {
  getTeams: (projectId: string) => api.get(`/projects/${projectId}/teams`),
  addTeam: (projectId: string, teamId: string) => api.post(`/projects/${projectId}/teams`, { teamId }),
  removeTeam: (projectId: string, teamId: string) => api.delete(`/projects/${projectId}/teams/${teamId}`),
};

// List members API
export const listMembersApi = {
  getMembers: (listId: string) => api.get(`/lists/${listId}/members`),
  addMember: (listId: string, userId: string) => api.post(`/lists/${listId}/members`, { userId }),
  removeMember: (listId: string, userId: string) => api.delete(`/lists/${listId}/members/${userId}`),
  restoreMember: (listId: string, userId: string) => api.post(`/lists/${listId}/members/${userId}/restore`),
  inheritFromProject: (listId: string) =>
    api.post(`/lists/${listId}/members/inherit-from-project`),
};

// View preferences API
export type ViewType = 'board' | 'list' | 'workload';
export type ViewPreferenceScopeType = 'project' | 'list';
export interface ViewPreferenceQuery {
  scopeType: ViewPreferenceScopeType;
  scopeId: string;
  viewType: ViewType;
}

export interface ViewPreferencePayload extends ViewPreferenceQuery {
  preferences: Record<string, unknown>;
}

export const viewPreferencesApi = {
  get: (query: ViewPreferenceQuery) =>
    api.get<{
      preference: {
        scopeType: ViewPreferenceScopeType;
        scopeId: string;
        viewType: ViewType;
        preferences: Record<string, unknown>;
        createdAt: string;
        updatedAt: string;
      } | null;
    }>('/view-preferences', { params: query }),
  upsert: (data: ViewPreferencePayload) =>
    api.put<{ success: boolean }>('/view-preferences', data),
  clear: (query: ViewPreferenceQuery) =>
    api.delete('/view-preferences', { params: query }),
};

// Effective assignees API
export type AccessSource = 'project' | 'team' | 'list' | 'admin';

export interface EffectiveUser {
  id: string;
  email: string;
  displayName: string;
  avatarUrl: string | null;
  role: 'admin' | 'user' | 'client';
  accessSource: AccessSource;
}

export interface EffectiveTeam {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
}

export interface EffectiveAssigneesResponse {
  users: EffectiveUser[];
  teams: EffectiveTeam[];
}

export const listAssigneesApi = {
  getAssignees: (listId: string, search?: string) =>
    api.get<EffectiveAssigneesResponse>(`/lists/${listId}/assignees`, {
      params: search ? { search } : undefined,
    }),
  getEffectiveMembers: (listId: string) =>
    api.get<{
      directMembers: Array<{
        id: string;
        userId: string;
        createdAt: string;
        user: { id: string; email: string; displayName: string; avatarUrl: string | null; role: string };
      }>;
      inheritedMembers: EffectiveUser[];
      teams: EffectiveTeam[];
    }>(`/lists/${listId}/effective-members`),
};

// Permissions API
export interface PermissionMatrixResponse {
  capabilities: Array<{
    id: string;
    key: string;
    label: string;
    category: string;
    description: string | null;
    isProtected: boolean;
  }>;
  matrix: Record<string, Record<string, boolean>>;
}

export interface UserCapabilitiesResponse {
  capabilities: Record<string, boolean>;
}

export interface UpdateRoleCapabilityData {
  role: string;
  capabilityKey: string;
  allowed: boolean;
}

export const permissionsApi = {
  getCapabilities: () => api.get<PermissionMatrixResponse['capabilities']>('/permissions/capabilities'),
  getMatrix: () => api.get<PermissionMatrixResponse>('/permissions/matrix'),
  getMyCapabilities: () => api.get<UserCapabilitiesResponse>('/permissions/me'),
  updateRoleCapability: (data: UpdateRoleCapabilityData) =>
    api.patch<{ success: boolean }>('/permissions/matrix', data),
  bulkUpdateRoleCapabilities: (updates: UpdateRoleCapabilityData[]) =>
    api.patch<{ success: boolean; updatedCount: number }>('/permissions/matrix/bulk', { updates }),
};

// ============================================
// CHAT APIs
// ============================================

import type {
  ProjectChat,
  ProjectChatMessage,
  ProjectChatAccess,
  ProjectChatSettings,
  ProjectChatPermissionOverride,
  CompanyChat,
  CompanyChatMessage,
  CompanyChatAccess,
  CompanyChatSettings,
  CompanyChatMember,
} from '@/types';

// Project Chats API
export const projectChatsApi = {
  getChats: (projectId: string) =>
    api.get<ProjectChat[]>(`/projects/${projectId}/chats`),

  getAccess: (chatId: string) =>
    api.get<ProjectChatAccess>(`/project-chats/${chatId}/access`),

  getMessages: (chatId: string, params?: { limit?: number; before?: string }) =>
    api.get<ProjectChatMessage[]>(`/project-chats/${chatId}/messages`, { params }),

  sendMessage: (chatId: string, content: string) =>
    api.post<ProjectChatMessage>(`/project-chats/${chatId}/messages`, { content }),

  getSettings: (chatId: string) =>
    api.get<ProjectChatSettings>(`/project-chats/${chatId}/settings`),

  updateSettings: (chatId: string, data: { name?: string }) =>
    api.patch<ProjectChat>(`/project-chats/${chatId}/settings`, data),

  getOverrides: (chatId: string) =>
    api.get<ProjectChatPermissionOverride[]>(`/project-chats/${chatId}/members`),

  getEffectiveMembers: (chatId: string) =>
    api.get<Array<{
      id: string;
      email: string;
      displayName: string;
      avatarUrl: string | null;
      canRead: boolean;
      canWrite: boolean;
    }>>(`/project-chats/${chatId}/effective-members`),

  addOverride: (chatId: string, data: { userId: string; canRead: boolean; canWrite: boolean }) =>
    api.post<ProjectChatPermissionOverride>(`/project-chats/${chatId}/members`, data),

  updateOverride: (chatId: string, userId: string, data: { canRead?: boolean; canWrite?: boolean }) =>
    api.patch<ProjectChatPermissionOverride>(`/project-chats/${chatId}/members/${userId}`, data),

  removeOverride: (chatId: string, userId: string) =>
    api.delete(`/project-chats/${chatId}/members/${userId}`),
};

// Company Chats API
export const companyChatsApi = {
  getChats: () =>
    api.get<CompanyChat[]>('/company-chats'),

  createChat: (data: { name: string; description?: string }) =>
    api.post<CompanyChat>('/company-chats', data),

  getChat: (chatId: string) =>
    api.get<CompanyChat>(`/company-chats/${chatId}`),

  updateChat: (chatId: string, data: { name?: string; description?: string }) =>
    api.patch<CompanyChat>(`/company-chats/${chatId}`, data),

  deleteChat: (chatId: string) =>
    api.delete(`/company-chats/${chatId}`),

  getAccess: (chatId: string) =>
    api.get<CompanyChatAccess>(`/company-chats/${chatId}/access`),

  getMessages: (chatId: string, params?: { limit?: number; before?: string }) =>
    api.get<CompanyChatMessage[]>(`/company-chats/${chatId}/messages`, { params }),

  sendMessage: (chatId: string, content: string) =>
    api.post<CompanyChatMessage>(`/company-chats/${chatId}/messages`, { content }),

  getSettings: (chatId: string) =>
    api.get<CompanyChatSettings>(`/company-chats/${chatId}/settings`),

  getMembers: (chatId: string) =>
    api.get<CompanyChatMember[]>(`/company-chats/${chatId}/members`),

  addMember: (chatId: string, data: { userId: string; canWrite?: boolean }) =>
    api.post<CompanyChatMember>(`/company-chats/${chatId}/members`, data),

  updateMember: (chatId: string, userId: string, data: { canWrite?: boolean }) =>
    api.patch<CompanyChatMember>(`/company-chats/${chatId}/members/${userId}`, data),

  removeMember: (chatId: string, userId: string) =>
    api.delete(`/company-chats/${chatId}/members/${userId}`),

  join: (chatId: string) =>
    api.post<CompanyChatMember>(`/company-chats/${chatId}/join`),

  leave: (chatId: string) =>
    api.post(`/company-chats/${chatId}/leave`),

  markAsRead: (chatId: string) =>
    api.post(`/company-chats/${chatId}/read`),

  findOrCreatePrivateChat: (targetUserId: string) =>
    api.post<CompanyChat>(`/company-chats/private`, { targetUserId }),
};

// ============================================
// ALL CHATS API (Unified view)
// ============================================

import type { UnifiedChat } from '@/types';

export const allChatsApi = {
  getAll: () =>
    api.get<UnifiedChat[]>('/all-chats'),

  markProjectChatAsRead: (chatId: string) =>
    api.post(`/all-chats/project/${chatId}/read`),
};

// ============================================
// TAGS API
// ============================================

import type { TagSet, Tag, ListTagSet, EffectiveTag, TaskTag } from '@/types';

// ============================================
// SECURITY API TYPES
// ============================================

export interface SecurityEvent {
  id: string;
  ipAddress: string;
  method: string;
  path: string;
  statusCode: number;
  reason: string;
  userAgent: string | null;
  userId: string | null;
  createdAt: string;
}

export interface BlockedIp {
  id: string;
  ipAddress: string;
  reason: string;
  blockedUntil: string | null;
  createdById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SecurityEventsParams {
  ipAddress?: string;
  reason?: string;
  limit?: number;
  offset?: number;
}

export interface SecurityEventsResponse {
  events: SecurityEvent[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateBlockedIpData {
  ipAddress: string;
  reason: string;
  blockedUntil?: string;
}

export const tagSetsApi = {
  getAll: () => api.get<TagSet[]>('/tag-sets'),
  getById: (id: string) => api.get<TagSet>(`/tag-sets/${id}`),
  create: (data: { name: string; description?: string }) =>
    api.post<TagSet>('/tag-sets', data),
  update: (id: string, data: { name?: string; description?: string }) =>
    api.patch<TagSet>(`/tag-sets/${id}`, data),
  delete: (id: string) => api.delete(`/tag-sets/${id}`),
  duplicate: (id: string) => api.post<TagSet>(`/tag-sets/${id}/duplicate`),
  getTags: (tagSetId: string) => api.get<Tag[]>(`/tag-sets/${tagSetId}/tags`),
  createTag: (tagSetId: string, data: { name: string; color?: string }) =>
    api.post<Tag>(`/tag-sets/${tagSetId}/tags`, data),
};

export const tagsApi = {
  update: (id: string, data: { name?: string; color?: string }) =>
    api.patch<Tag>(`/tags/${id}`, data),
  delete: (id: string) => api.delete(`/tags/${id}`),
};

export const listTagSetsApi = {
  getTagSets: (listId: string) => api.get<ListTagSet[]>(`/lists/${listId}/tag-sets`),
  assign: (listId: string, tagSetId: string) =>
    api.post<ListTagSet>(`/lists/${listId}/tag-sets`, { tagSetId }),
  remove: (listId: string, tagSetId: string) =>
    api.delete(`/lists/${listId}/tag-sets/${tagSetId}`),
  getEffectiveTags: (listId: string) =>
    api.get<EffectiveTag[] | { tags: EffectiveTag[] }>(`/lists/${listId}/tags`),
};

export const taskTagsApi = {
  getTags: (taskId: string) => api.get<TaskTag[]>(`/tasks/${taskId}/tags`),
  addTag: (taskId: string, tagId: string) =>
    api.post<TaskTag>(`/tasks/${taskId}/tags`, { tagId }),
  removeTag: (taskId: string, tagId: string) =>
    api.delete(`/tasks/${taskId}/tags/${tagId}`),
};

// ============================================
// SECURITY API
// ============================================

export const securityApi = {
  getEvents: (params?: SecurityEventsParams) =>
    api.get<SecurityEventsResponse>('/security/events', { params }),
  getBlockedIps: () =>
    api.get<BlockedIp[]>('/security/blocked-ips'),
  blockIp: (data: CreateBlockedIpData) =>
    api.post<BlockedIp>('/security/blocked-ips', data),
  unblockIp: (id: string) =>
    api.delete<{ success: boolean }>(`/security/blocked-ips/${id}`),
};

// ============================================
// WORKGROUPS API
// ============================================

export type WorkGroupType = 'project' | 'standalone' | 'private';

export interface WorkGroupSummary {
  id: string;
  type: WorkGroupType;
  name: string | null;
  projectId: string | null;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  ownerId?: string | null;
}

export interface WorkGroupQuota {
  userId: string;
  quotaBytes: string;
  usedBytes: string;
}

export type WorkGroupFolderPermissionLevel = 'none' | 'read' | 'write' | 'manage';

export interface WorkGroupFolderMetadata {
  id: string;
  workGroupId: string;
  parentId: string | null;
  name: string;
  isRoot: boolean;
  deletedAt: string | null;
  updatedAt: string;
  deleteBatchId?: string | null;
}

export interface WorkGroupFileListItem {
  id: string;
  folderId: string;
  originalName: string;
  displayName: string | null;
  mimeType: string;
  status: string;
  sizeBytes: number | null;
  currentVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WorkGroupContentsResponse {
  folder: WorkGroupFolderMetadata;
  effectivePermission: WorkGroupFolderPermissionLevel;
  childFolders: WorkGroupFolderMetadata[];
  files: WorkGroupFileListItem[];
  depth: number;
  maxDepth: number;
  canCreateChild: boolean;
}

export interface WorkGroupTrashUserSummary {
  id: string;
  displayName: string;
  email: string;
}

export interface WorkGroupTrashFileEntry {
  type: 'file';
  id: string;
  folderId: string;
  name: string;
  mimeType: string;
  sizeBytes: number | null;
  status: string;
  deletedAt: string;
  deleteBatchId: string | null;
  deletedBy: WorkGroupTrashUserSummary | null;
}

export interface WorkGroupTrashFolderEntry {
  type: 'folder';
  id: string;
  parentId: string | null;
  name: string;
  deletedAt: string;
  deleteBatchId: string | null;
  deletedBy: WorkGroupTrashUserSummary | null;
}

export interface WorkGroupTrashListResponse {
  limit: number;
  folders: WorkGroupTrashFolderEntry[];
  files: WorkGroupTrashFileEntry[];
}

export interface WorkGroupMemberSummary {
  id: string;
  workGroupId: string;
  userId: string;
  role: string;
  createdAt: string;
  email: string;
}

export interface WorkGroupFolderPermissionRow {
  id: string;
  folderId: string;
  userId: string;
  level: WorkGroupFolderPermissionLevel;
  createdAt: string;
  updatedAt: string;
  user: { id: string; email: string; displayName: string | null };
}

export interface WorkGroupFileMetadata {
  id: string;
  workGroupId: string;
  folderId: string;
  originalName: string;
  displayName: string | null;
  mimeType: string;
  status: string;
  sizeBytes: number | null;
  currentVersionId: string | null;
  createdById: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  deleteBatchId?: string | null;
}

export interface WorkGroupFileVersionListItem {
  id: string;
  versionNumber: number;
  sizeBytes: number;
  mimeType: string;
  createdAt: string;
  uploadedBy: { id: string; displayName: string; email: string };
  isCurrent: boolean;
}

export interface WorkGroupDownloadJsonInfo {
  url: string;
  fileName: string;
  mimeType: string;
  openMode: string;
}

export interface WorkGroupAdminQuotaRow {
  userId: string;
  displayName: string | null;
  email: string;
  quotaBytes: string;
  usedBytes: string;
  updatedAt: string;
}

export interface WorkGroupContentsQueryParams {
  folderId?: string;
  includeDeleted?: boolean;
  cursor?: string;
  limit?: number;
}

export interface WorkGroupTrashQueryParams {
  limit?: number;
}

function workgroupsPrivateParams(privateAccessReason?: string) {
  return privateAccessReason ? { privateAccessReason } : {};
}

export const workgroupsApi = {
  listWorkGroups: () => api.get<WorkGroupSummary[]>('/workgroups'),

  getWorkGroup: (workGroupId: string, privateAccessReason?: string) =>
    api.get<WorkGroupSummary>(`/workgroups/${workGroupId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  getMyPrivateWorkGroup: () => api.get<WorkGroupSummary>('/workgroups/me/private'),

  getMyQuota: () => api.get<WorkGroupQuota>('/workgroups/me/quota'),

  getOrCreateProjectWorkGroup: (projectId: string) =>
    api.get<WorkGroupSummary>(`/workgroups/projects/${projectId}`),

  getContents: (
    workGroupId: string,
    query?: WorkGroupContentsQueryParams,
    privateAccessReason?: string,
  ) =>
    api.get<WorkGroupContentsResponse>(`/workgroups/${workGroupId}/contents`, {
      params: {
        ...(query ?? {}),
        ...workgroupsPrivateParams(privateAccessReason),
      },
    }),

  createStandaloneWorkGroup: (data: { name: string; type?: 'standalone' }) =>
    api.post<WorkGroupSummary>('/workgroups', data),

  updateWorkGroup: (workGroupId: string, data: { name?: string }) =>
    api.patch<WorkGroupSummary>(`/workgroups/${workGroupId}`, data),

  archiveWorkGroup: (workGroupId: string, privateAccessReason?: string) =>
    api.patch<WorkGroupSummary>(`/workgroups/${workGroupId}/archive`, undefined, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  unarchiveWorkGroup: (workGroupId: string, privateAccessReason?: string) =>
    api.patch<WorkGroupSummary>(`/workgroups/${workGroupId}/unarchive`, undefined, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  deleteWorkGroup: (workGroupId: string, privateAccessReason?: string) =>
    api.delete<WorkGroupSummary>(`/workgroups/${workGroupId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  listMembers: (workGroupId: string) =>
    api.get<WorkGroupMemberSummary[]>(`/workgroups/${workGroupId}/members`),

  addMember: (workGroupId: string, data: { userId: string }) =>
    api.post<WorkGroupMemberSummary>(`/workgroups/${workGroupId}/members`, data),

  updateMemberRole: (workGroupId: string, userId: string, data: { role: string }) =>
    api.patch<WorkGroupMemberSummary>(`/workgroups/${workGroupId}/members/${userId}`, data),

  removeMember: (workGroupId: string, userId: string) =>
    api.delete(`/workgroups/${workGroupId}/members/${userId}`),

  listFolderPermissions: (
    workGroupId: string,
    folderId: string,
    privateAccessReason?: string,
  ) =>
    api.get<WorkGroupFolderPermissionRow[]>(
      `/workgroups/${workGroupId}/folders/${folderId}/permissions`,
      { params: workgroupsPrivateParams(privateAccessReason) },
    ),

  upsertFolderPermission: (
    workGroupId: string,
    folderId: string,
    data: { userId: string; level: WorkGroupFolderPermissionLevel },
    privateAccessReason?: string,
  ) =>
    api.post<WorkGroupFolderPermissionRow>(
      `/workgroups/${workGroupId}/folders/${folderId}/permissions`,
      data,
      { params: workgroupsPrivateParams(privateAccessReason) },
    ),

  deleteFolderPermission: (
    workGroupId: string,
    folderId: string,
    targetUserId: string,
    privateAccessReason?: string,
  ) =>
    api.delete(
      `/workgroups/${workGroupId}/folders/${folderId}/permissions/${targetUserId}`,
      { params: workgroupsPrivateParams(privateAccessReason) },
    ),

  listTrash: (
    workGroupId: string,
    query?: WorkGroupTrashQueryParams,
    privateAccessReason?: string,
  ) =>
    api.get<WorkGroupTrashListResponse>(`/workgroups/${workGroupId}/trash`, {
      params: {
        ...(query ?? {}),
        ...workgroupsPrivateParams(privateAccessReason),
      },
    }),

  emptyTrash: (workGroupId: string, privateAccessReason?: string) =>
    api.delete<{ purgedFileCount: number; purgedFolderCount?: number; freedBytes: string }>(
      `/workgroups/${workGroupId}/trash`,
      {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  getFileMetadata: (workGroupId: string, fileId: string, privateAccessReason?: string) =>
    api.get<WorkGroupFileMetadata>(`/workgroups/${workGroupId}/files/${fileId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  getFileVersions: (workGroupId: string, fileId: string, privateAccessReason?: string) =>
    api.get<WorkGroupFileVersionListItem[]>(`/workgroups/${workGroupId}/files/${fileId}/versions`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  downloadFile: (workGroupId: string, fileId: string, privateAccessReason?: string) =>
    api.get<Blob>(`/workgroups/${workGroupId}/files/${fileId}/download`, {
      responseType: 'blob',
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  getDownloadFileAsJson: (workGroupId: string, fileId: string, privateAccessReason?: string) =>
    api.get<WorkGroupDownloadJsonInfo>(`/workgroups/${workGroupId}/files/${fileId}/download`, {
      params: { as: 'json', ...workgroupsPrivateParams(privateAccessReason) },
    }),

  downloadFileVersion: (
    workGroupId: string,
    fileId: string,
    versionId: string,
    privateAccessReason?: string,
  ) =>
    api.get<Blob>(
      `/workgroups/${workGroupId}/files/${fileId}/versions/${versionId}/download`,
      {
        responseType: 'blob',
        params: workgroupsPrivateParams(privateAccessReason),
      },
    ),

  getDownloadFileVersionAsJson: (
    workGroupId: string,
    fileId: string,
    versionId: string,
    privateAccessReason?: string,
  ) =>
    api.get<WorkGroupDownloadJsonInfo>(
      `/workgroups/${workGroupId}/files/${fileId}/versions/${versionId}/download`,
      {
        params: { as: 'json', ...workgroupsPrivateParams(privateAccessReason) },
      },
    ),

  restoreFileVersion: (
    workGroupId: string,
    fileId: string,
    versionId: string,
    privateAccessReason?: string,
  ) =>
    api.patch<WorkGroupFileMetadata>(
      `/workgroups/${workGroupId}/files/${fileId}/versions/${versionId}/restore`,
      undefined,
      { params: workgroupsPrivateParams(privateAccessReason) },
    ),

  uploadNewFileVersion: (
    workGroupId: string,
    fileId: string,
    file: File,
    privateAccessReason?: string,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<WorkGroupFileMetadata>(
      `/workgroups/${workGroupId}/files/${fileId}/versions`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        params: workgroupsPrivateParams(privateAccessReason),
      },
    );
  },

  createFolder: (
    workGroupId: string,
    data: { name: string; parentId?: string },
    privateAccessReason?: string,
  ) =>
    api.post<WorkGroupFolderMetadata>(`/workgroups/${workGroupId}/folders`, data, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  updateFolder: (
    workGroupId: string,
    folderId: string,
    data: { name: string },
    privateAccessReason?: string,
  ) =>
    api.patch<WorkGroupFolderMetadata>(`/workgroups/${workGroupId}/folders/${folderId}`, data, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  deleteFolder: (workGroupId: string, folderId: string, privateAccessReason?: string) =>
    api.delete(`/workgroups/${workGroupId}/folders/${folderId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  trashFile: (workGroupId: string, fileId: string, privateAccessReason?: string) =>
    api.delete<WorkGroupFileMetadata>(`/workgroups/${workGroupId}/files/${fileId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  uploadFile: (
    workGroupId: string,
    file: File,
    folderId?: string,
    privateAccessReason?: string,
    onUploadProgress?: (percent: number | null) => void,
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post<WorkGroupFileMetadata>(`/workgroups/${workGroupId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: {
        ...(folderId ? { folderId } : {}),
        ...workgroupsPrivateParams(privateAccessReason),
      },
      onUploadProgress: onUploadProgress
        ? (event) => {
            if (event.total) {
              onUploadProgress(Math.min(100, Math.round((event.loaded * 100) / event.total)));
            } else {
              onUploadProgress(null);
            }
          }
        : undefined,
    });
  },

  restoreTrashFile: (workGroupId: string, fileId: string, privateAccessReason?: string) =>
    api.patch<WorkGroupFileMetadata>(
      `/workgroups/${workGroupId}/trash/files/${fileId}/restore`,
      undefined,
      { params: workgroupsPrivateParams(privateAccessReason) },
    ),

  permanentlyDeleteTrashFile: (
    workGroupId: string,
    fileId: string,
    privateAccessReason?: string,
  ) =>
    api.delete(`/workgroups/${workGroupId}/trash/files/${fileId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  restoreTrashFolder: (workGroupId: string, folderId: string, privateAccessReason?: string) =>
    api.patch<WorkGroupFolderMetadata>(
      `/workgroups/${workGroupId}/trash/folders/${folderId}/restore`,
      undefined,
      { params: workgroupsPrivateParams(privateAccessReason) },
    ),

  permanentlyDeleteTrashFolder: (
    workGroupId: string,
    folderId: string,
    privateAccessReason?: string,
  ) =>
    api.delete(`/workgroups/${workGroupId}/trash/folders/${folderId}`, {
      params: workgroupsPrivateParams(privateAccessReason),
    }),

  listAdminQuotas: () => api.get<WorkGroupAdminQuotaRow[]>('/admin/workgroups/quotas'),

  updateAdminQuota: (userId: string, data: { quotaBytes: string }) =>
    api.patch<WorkGroupAdminQuotaRow>(`/admin/workgroups/quotas/${userId}`, data),
};
